import ApiService from "./ApiService";
import { USER_TYPES } from "../assets/appConstants";
import {
  mapTherapistMeToWizardAccount,
  normalizeV2TherapistDto,
  pickInvitedSubAccount,
} from "../components/therapist/registrationWizard/registrationWizardBootstrap";
import {
  isClinicianAccountType,
  resolveAccountContext,
} from "./accountTypeResolver";

const INVITED_STATUSES = new Set(["INVITED", "invited"]);

const isValidSubAccountId = (id) => id != null && id !== "";

/** Parent apps expect login-helper's canonical type: therapist (not URL hint "clinician"). */
const toLoginAccountType = (accountType) => {
  if (isClinicianAccountType(accountType)) {
    return USER_TYPES.THERAPIST;
  }
  return accountType || USER_TYPES.THERAPIST;
};

export const isClinicianRegistrationPending = (clinicianDto, accountContext = {}) => {
  // Explicit completed signal from my/type → main-app login, not onboarding.
  if (accountContext.registrationCompleted === true) {
    return false;
  }
  if (accountContext.needToSetPassword === true) {
    return true;
  }
  if (INVITED_STATUSES.has(accountContext.subAccountStatus)) {
    return true;
  }

  if (!clinicianDto) {
    return false;
  }

  if (clinicianDto.subAccountForRegistration) {
    return true;
  }

  const flat = normalizeV2TherapistDto(clinicianDto);
  if (INVITED_STATUSES.has(flat.subAccountStatus)) {
    return true;
  }
  if (flat.needToSetPassword === true) {
    return true;
  }

  return false;
};

export const pickSubAccountId = (...sources) => {
  for (const source of sources) {
    if (!source) {
      continue;
    }

    if (isValidSubAccountId(source.subAccountId)) {
      return source.subAccountId;
    }
    if (isValidSubAccountId(source.defaultSubAccountId)) {
      return source.defaultSubAccountId;
    }

    const flat = normalizeV2TherapistDto(source);
    if (isValidSubAccountId(flat.subAccountId)) {
      return flat.subAccountId;
    }

    const subAccount = pickInvitedSubAccount(source);
    if (isValidSubAccountId(subAccount?.accountId)) {
      return subAccount.accountId;
    }

    if (isValidSubAccountId(source.account?.defaultSubAccountId)) {
      return source.account.defaultSubAccountId;
    }
  }
  return null;
};

const isJwtString = (value) =>
  typeof value === "string" && value.trim().split(".").length === 3;

/** v2 custom-token may return { customToken }, { token }, or the JWT string in data. */
export const parseCustomTokenResponse = (tokenRes, depth = 0) => {
  if (!tokenRes || depth > 4) {
    return null;
  }
  if (isJwtString(tokenRes)) {
    return tokenRes.trim();
  }
  if (typeof tokenRes !== "object") {
    return null;
  }
  for (const key of [
    "customToken",
    "token",
    "firebaseToken",
    "firebaseCustomToken",
    "accessToken",
    "idToken",
    "value",
  ]) {
    if (isJwtString(tokenRes[key])) {
      return tokenRes[key].trim();
    }
  }
  if (tokenRes.data != null) {
    const fromData = parseCustomTokenResponse(tokenRes.data, depth + 1);
    if (fromData) {
      return fromData;
    }
  }
  for (const value of Object.values(tokenRes)) {
    if (value && typeof value === "object") {
      const nested = parseCustomTokenResponse(value, depth + 1);
      if (nested) {
        return nested;
      }
    }
  }
  return null;
};

const normalizeCustomTokenResponse = (tokenRes) => {
  const customToken = parseCustomTokenResponse(tokenRes);
  if (typeof tokenRes === "object" && tokenRes) {
    return customToken ? { ...tokenRes, customToken } : { ...tokenRes };
  }
  return customToken ? { customToken } : {};
};

export const fetchClinicianCustomToken = async (subAccountId) => {
  if (!subAccountId) {
    throw new Error("subAccountId is required for clinician custom token");
  }
  const tokenRes = await ApiService.getCustomTokenForSubAccount(subAccountId);
  return normalizeCustomTokenResponse(tokenRes);
};

const buildCompletedLoginData = ({
  accountId,
  accountType,
  customToken,
  subAccountId,
  tokenRes = {},
}) => {
  const payload =
    tokenRes && typeof tokenRes === "object" ? { ...tokenRes } : {};

  // Parent host still gates redirect on login-helper fields.
  // Prefer any encryptedAccountId from the token API; otherwise use subAccountId
  // as a compatibility shim (login app itself does not read this field).
  const encryptedAccountId =
    payload.encryptedAccountId || subAccountId || accountId || null;

  return {
    ...payload,
    accountId,
    accountType: toLoginAccountType(accountType),
    customToken,
    subAccountId: subAccountId || null,
    encryptedAccountId,
    accountData: null,
    immediateRedirect: true,
    registrationCompleted: true,
  };
};

const buildOnboardingLoginData = ({ accountId, accountType, customToken, clinicianDto }) => {
  const wizardAccount = mapTherapistMeToWizardAccount(clinicianDto, accountId);
  return {
    accountId,
    accountType: toLoginAccountType(accountType),
    customToken,
    accountData: {
      ...wizardAccount,
      accountId,
    },
    immediateRedirect: false,
    registrationCompleted: false,
  };
};

const canSkipTherapistMe = (accountContext = {}) =>
  accountContext.registrationCompleted === true &&
  accountContext.needToSetPassword !== true &&
  isValidSubAccountId(accountContext.defaultSubAccountId);

/**
 * Clinician login/session resolution (replaces login-helper for clinicians).
 *
 * Pass `accountContext` from the caller when already resolved — avoids a second
 * GET /v2/accounts/my/type.
 *
 * 1. Account type / defaultSubAccountId: accounts/my/type (+ URL hint / claims)
 * 2. Profile / pending flags: GET /v2/therapists/me (skipped when my/type already says complete)
 * 3. Sub-account token: GET /v2/accounts/custom-token/:subAccountId
 *
 * Do NOT call therapists/account/for-registration here — that is registration-only
 * (wizard bootstrap / invite) after the sub-account token is in Firebase.
 */
export const resolveClinicianLoginSession = async (user, options = {}) => {
  const accountId = user?.uid;
  if (!accountId) {
    throw new Error("Authenticated Firebase user is required");
  }

  const accountContext =
    options.accountContext ||
    (await resolveAccountContext(user, options.accountTypeHint));
  const accountType = accountContext.accountType;
  if (!isClinicianAccountType(accountType)) {
    throw new Error("resolveClinicianLoginSession called for non-clinician account");
  }

  let therapistMe = options.therapistMe ?? null;
  if (
    therapistMe == null &&
    (options.forceTherapistMe === true || !canSkipTherapistMe(accountContext))
  ) {
    try {
      therapistMe = await ApiService.getTherapistMe();
      if (therapistMe === false) {
        therapistMe = null;
      }
    } catch (error) {
      ApiService.logDispatcherError("v2 therapists/me unavailable during login", error);
      therapistMe = null;
    }
  }

  const registrationPending = (() => {
    // An OTL URL is explicitly a regular-login flow. Registration links use
    // registrationCode and never enter this session resolver.
    if (options.forceRegularLogin === true) {
      return false;
    }
    if (accountContext.needToSetPassword === true) {
      return true;
    }
    if (accountContext.registrationCompleted === true) {
      return false;
    }
    // SAMC main re-login: my/type already has a default sub-account → main app.
    if (isValidSubAccountId(accountContext.defaultSubAccountId)) {
      return false;
    }
    return isClinicianRegistrationPending(therapistMe, accountContext);
  })();

  // Completed login: prefer defaultSubAccountId. Pending: prefer invited sub-account from me.
  const subAccountId = registrationPending
    ? pickSubAccountId(
        therapistMe,
        { defaultSubAccountId: accountContext.defaultSubAccountId },
        options
      )
    : pickSubAccountId(
        { defaultSubAccountId: accountContext.defaultSubAccountId },
        therapistMe,
        options
      );
  if (!subAccountId) {
    throw new Error("No clinician sub-account found for login");
  }

  const tokenRes = await fetchClinicianCustomToken(subAccountId);
  const customToken =
    parseCustomTokenResponse(tokenRes) || parseCustomTokenResponse(options.customToken);
  if (!customToken) {
    throw new Error("Unable to resolve clinician custom token");
  }

  if (registrationPending) {
    return {
      customToken,
      therapistMe,
      loginData: buildOnboardingLoginData({
        accountId,
        accountType,
        customToken,
        clinicianDto: therapistMe,
      }),
    };
  }

  return {
    customToken,
    therapistMe,
    loginData: buildCompletedLoginData({
      accountId,
      accountType,
      customToken,
      subAccountId,
      tokenRes,
    }),
  };
};
